/**
 * Formats elapsed milliseconds as MM:SS.
 */
export function formatDuration(ms) {
	const totalSec = Math.floor(ms / 1000);
	const min = Math.floor(totalSec / 60);
	const sec = totalSec % 60;
	return `${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

/**
 * Returns German label for call state + direction.
 */
export function getCallLabel(callState, direction) {
	switch (callState) {
		case 'ringing_inbound':
			return 'Eingehender Anruf';
		case 'ringing_outbound':
			return 'Wird gewählt\u2026';
		case 'active':
			return direction === 'inbound' ? 'Eingehender Anruf aktiv' : 'Anruf aktiv';
		case 'ended':
			return 'Anruf beendet';
		default:
			return '';
	}
}

/**
 * Returns the relevant phone number to display based on direction.
 */
export function getDisplayNumber(activeCall) {
	if (!activeCall) return null;
	return activeCall.direction === 'inbound' ? activeCall.from : activeCall.to;
}

/**
 * Resolves the text shown in the overlay's duration slot for the current call phase.
 * Ringing has no duration yet (shows "Klingelt…"); active shows the live talk timer;
 * ended shows the SDK-provided duration (authoritative), falling back to the last timer value.
 *
 * @param {Object} params
 * @param {boolean} params.isRinging
 * @param {boolean} params.isActive
 * @param {boolean} params.isEnded
 * @param {number} params.elapsed - Live talk time in ms from the client timer.
 * @param {{ duration?: number|null }} [params.activeCall] - duration is in seconds.
 * @returns {string|null} Display string, or null when there is nothing to show.
 */
export function getDurationText({ isRinging, isActive, isEnded, elapsed, activeCall }) {
	if (isRinging) return 'Klingelt…';
	if (isActive) return formatDuration(elapsed);
	if (isEnded) {
		const sdkDuration = activeCall?.duration;
		const sdkDurationMs = sdkDuration === null || sdkDuration === undefined ? null : sdkDuration * 1000;
		return formatDuration(sdkDurationMs ?? elapsed);
	}
	return null;
}

/**
 * Build a full name from first/last, or null when neither is set.
 */
function fullName(contact) {
	const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ').trim();
	return name || null;
}

/**
 * Group a phone-lookup result into display "parties" for the call overlay.
 *
 * Matched companies and matched contacts are merged and grouped by company so
 * the overlay can show "Firma › Kontakt(e)" cleanly. A matched contact whose own
 * company did not match the number is still grouped under its company (by
 * crmCompanyId + companyName). Contacts without any company become their own
 * party with no company link.
 *
 * @param {{
 *   contacts?: Array<{id: string, firstName: string|null, lastName: string|null, companyRole: string|null, crmCompanyId: string|null, companyName: string|null}>,
 *   companies?: Array<{id: string, companyName: string|null}>
 * }} [lookup]
 * @returns {Array<{
 *   companyId: string|null,
 *   companyName: string|null,
 *   contacts: Array<{id: string, name: string|null, role: string|null}>
 * }>} One entry per company (or per orphan contact), in a stable order
 */
export function groupLookupParties(lookup) {
	const contacts = lookup?.contacts ?? [];
	const companies = lookup?.companies ?? [];

	// Seed groups from directly-matched companies (preserves them even with no matched contact).
	const groups = new Map();
	for (const co of companies) {
		groups.set(co.id, { companyId: co.id, companyName: co.companyName, contacts: [] });
	}

	const orphans = [];
	for (const c of contacts) {
		const entry = { id: c.id, name: fullName(c), role: c.companyRole || null };
		if (!c.crmCompanyId) {
			orphans.push({ companyId: null, companyName: null, contacts: [entry] });
			continue;
		}
		if (!groups.has(c.crmCompanyId)) {
			groups.set(c.crmCompanyId, { companyId: c.crmCompanyId, companyName: c.companyName, contacts: [] });
		}
		groups.get(c.crmCompanyId).contacts.push(entry);
	}

	return [...groups.values(), ...orphans];
}

/**
 * Returns Tailwind color classes based on call state.
 */
export function getStateColors(isRinging, isActive) {
	if (isRinging) {
		return {
			bg: 'bg-caution-200',
			text: 'text-caution-700',
			dot: 'bg-caution-500',
			ping: 'bg-caution-500',
			headerBg: 'bg-caution-200',
			headerText: 'text-caution-700',
			bar: 'bg-caution-500 animate-pulse',
		};
	}
	if (isActive) {
		return {
			bg: 'bg-success-200',
			text: 'text-success-700',
			dot: 'bg-success-500',
			ping: 'bg-success-500',
			headerBg: 'bg-success-200',
			headerText: 'text-success-700',
			bar: 'bg-success-500',
		};
	}
	return {
		bg: 'bg-bgBase-400',
		text: 'text-bgBase-800',
		dot: 'bg-bgBase-600',
		ping: '',
		headerBg: 'bg-bgBase-300',
		headerText: 'text-text',
		bar: 'bg-bgBase-600',
	};
}
